import { useEffect, useMemo, useState } from 'react';
import { ScreenSkeleton } from '../../../../components/atoms/ScreenSkeleton';
import { useParams, useNavigate } from 'react-router';
import { ScreenHeading } from '../../../../components/molecules/ScreenHeading';
import { ActionStatusBar } from '../../../../components/molecules/ActionStatusBar';
import { ObservableLevelsEnum, PartName } from '../../../../data/enums';
import { ScreenObservationLayout } from '../../../../components/templates/ScreenObservationLayout';
import { Label } from '../../../../components/atoms/Label';
import {
  SingleLineTextField,
  SingleLineValidation,
} from '../../../../components/atoms/SingleLineTextField';
import { CheckboxWithComponents } from '../../../../components/atoms/CheckboxWithComponentsField';
import DropdownField from '../../../../components/atoms/DropdownField';
import PhotoSelector from '../../../../components/atoms/PhotoSelector';
import { RichTextField } from '../../../../components/atoms/RichTextField';
import { CompleteButton } from '../../../../components/atoms/Button';
import { ObservableItemIdentifier } from '../../../../api-contracts/other/ObservableItemIdentifier';
import { flattenObjectStrings } from '../../../../utils/flattenObjectString';
import { MdHelpers } from '../../../../helpers/mdHelpers';
import { ErrorBanner } from '../../../../components/atoms/ErrorBanner';
import useInspectionCategoryType from '../../../../hooks/useInspectionCategoryType';
import { inspectionDB } from '../../../../data/database.structure';
import { ActionTypeStore } from '../../../../store/inspection/observation/actionTypeStore';
import { useInspectionCached } from '../../../../hooks/useInspectionCached';
import { useAssetEquipmentPartCached } from '../../../../hooks/useAssetEquipmentPartCached';
import { ObservationDraftScope } from '../../../../components/ObservationDraftScope';
import usePartDimensionForPath from '../../../../hooks/usePartDimensionForPath';
import WearDisplayHelpers from '../../../../helpers/wearDisplayHelpers';

export const ShoeDriveLugScreen = () => {
  const { externalId, sideName, partCategory } = useParams();
  const navigate = useNavigate();

  const [measurementValidationError, setErrorValidation] =
    useState<SingleLineValidation>(null);

  const { inspection } = useInspectionCached(externalId);

  const undercarriageCategory = useInspectionCategoryType({
    inspection,
  });

  const {
    equipmentPart,
    error: errorEquipmentPart,
    numberOfShoes,
  } = useAssetEquipmentPartCached({
    externalId,
    asset: inspection?.asset,
    sideName,
    partCategory,
    partName: PartName.driveLug,
  });

  const [error, setError] = useState<string | null>(null);
  const [rangeError, setRangeError] = useState<string | null>(null);

  // #10205: acceptable range visible before the user types. DISPLAY-ONLY.
  const { partDimension, isLoaded: partDimensionLoaded } =
    usePartDimensionForPath({
      path: ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
        .WornLength.Measurement,
      equipmentPartExternalId: equipmentPart?.externalId,
    });

  useEffect(() => {
    setError(null); // reset error
    const getEquipment = () => {
      try {
        if (errorEquipmentPart) {
          setError(errorEquipmentPart);
          return;
        }
      } catch (error) {
        console.error('Error fetching equipment', error);
        setError('Error fetching equipment');
      }
    };

    getEquipment();
  }, [errorEquipmentPart, equipmentPart]);

  const { Undercarriage } = ObservableItemIdentifier.Identifiers;
  const listWearIndicators = useMemo(
    () => [
      {
        label: 'Plastic Deformation',
        path: Undercarriage.Shoes.DriveLug.WearIndicators.PlasticDeformation
          .Checked,
        childPaths: [
          Undercarriage.Shoes.DriveLug.WearIndicators.PlasticDeformation.Value,
        ],
      },
      {
        label: 'Spalling / Delamination / Minor Cracking',
        path: Undercarriage.Shoes.DriveLug.WearIndicators
          .SpallingDelaminationMinorCracking.Checked,
        childPaths: [
          Undercarriage.Shoes.DriveLug.WearIndicators
            .SpallingDelaminationMinorCracking.Value,
        ],
      },
      {
        label: 'Severe Cracking / Broken',
        path: Undercarriage.Shoes.DriveLug.WearIndicators.SevereCracking
          .Checked,
        childPaths: [
          Undercarriage.Shoes.DriveLug.WearIndicators.SevereCracking.Value,
        ],
      },
    ],
    [],
  );

  if (
    !equipmentPart ||
    !numberOfShoes ||
    !externalId ||
    !undercarriageCategory
  ) {
    return <ScreenSkeleton fields={4} />;
  }

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <ObservationDraftScope inspectionId={externalId}>
      <div className="w-full">
        <div className="sticky -top-4 z-10 bg-white pt-4">
          <ScreenHeading
            eyebrow={'Shoes'}
            title={'Drive lug'}
            tag={sideName?.split('-')[0]}
            partNumber={equipmentPart.part.partNumber}
            tooltipKey={'undercarriage-shoes-drive-lug-screen-header'}
          />

          <div className="-mx-4 mt-2 bg-white">
            <ActionStatusBar
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entities={[
                {
                  entityId: equipmentPart.id,
                  entityExternalId: equipmentPart.externalId,
                },
              ]}
              paths={[Undercarriage.DriveLug.WornLength.Measurement]}
              pathsLike={flattenObjectStrings(
                Undercarriage.Shoes.DriveLug.WearIndicators,
              )}
            />
          </div>
        </div>

        <ScreenObservationLayout>
          <Label titleType="fieldTitle" tooltipKey="shoes-worn-length">
            Measured Length
          </Label>

          <SingleLineTextField
            title="Measurement (mm)"
            path={Undercarriage.DriveLug.WornLength.Measurement}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            type="float"
            tooltipKey={Undercarriage.DriveLug.WornLength.Measurement}
            placeholder="Measurement (mm)"
            validationError={measurementValidationError}
            onChange={async (value) => {
              if (value === '') {
                return;
              }
              const valueFloat = parseFloat(value);
              if (isNaN(valueFloat)) {
                setErrorValidation('InvalidInput');
                setTimeout(() => {
                  setErrorValidation('');
                }, 1500);
                return;
              }
              const observableItem = await inspectionDB.observableItems
                .where('identifier')
                .equals(
                  ObservableItemIdentifier.Identifiers.Undercarriage.DriveLug
                    .WornLength.Measurement,
                )
                .first();
              if (!observableItem) {
                setErrorValidation('InvalidInput');
                setTimeout(() => {
                  setErrorValidation('');
                }, 1500);
                return;
              }
              const actionStatus =
                await new ActionTypeStore().getPartActionStatus({
                  equipmentPartId: equipmentPart.externalId,
                  value: valueFloat,
                  observableItemId: observableItem.id,
                });
              const isValid = ActionTypeStore.validateActionStatus({
                actionStatus: actionStatus,
                value: valueFloat,
              });
              if (
                !isValid &&
                (actionStatus.percentage < 0 || actionStatus.percentage > 250)
              ) {
                // #10205: rejection message includes the acceptable range
                setRangeError(
                  WearDisplayHelpers.formatOutOfRangeError({
                    value: valueFloat,
                    minValue: actionStatus.minValue,
                    maxValue: actionStatus.maxValue,
                  }),
                );
                setErrorValidation('BelowOrEqualZero');
                setTimeout(() => {
                  setErrorValidation('');
                }, 1500);
                return;
              }
              setRangeError(null);
            }}
          />
          {/* #10205: acceptable range visible before the user types */}
          {partDimensionLoaded && (
            <div className="text-sm text-inkMuted">
              {WearDisplayHelpers.formatAcceptableRangeHelperText({
                dimension: partDimension,
              })}
            </div>
          )}
          {rangeError && <p className="text-sm text-red-500">{rangeError}</p>}

          <Label
            titleType="fieldTitle"
            tooltipKey={'undercarriage-shoes-drive-lug-wear-indicators'}
          >
            Wear Indicators
          </Label>
          {listWearIndicators.map((obj, i) => (
            <CheckboxWithComponents
              key={obj.path + i}
              {...obj}
              dataLevel={ObservableLevelsEnum.equipmentPart}
              entityId={equipmentPart.id}
              entityExternalId={equipmentPart.externalId}
              inspectionId={externalId}
              inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
              infoTooltipKey={`undercarriage-shoes-drive-lug-wearindicators-${MdHelpers.normaliseTooltipKeyComponent(obj.label)}`}
            >
              <DropdownField
                options={Array.from({ length: numberOfShoes }, (_, i) =>
                  (i + 1).toString(),
                )}
                placeholder="Number of shoes"
                path={obj.childPaths[0]}
                dataLevel={ObservableLevelsEnum.equipmentPart}
                entityId={equipmentPart.id}
                entityExternalId={equipmentPart.externalId}
                inspectionId={externalId}
                inspectionCategoryTypeId={
                  undercarriageCategory?.externalId ?? ''
                }
              />
            </CheckboxWithComponents>
          ))}
          <PhotoSelector
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            title="Photos"
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
            path={Undercarriage.DriveLug.Photo}
          />
          <RichTextField
            className="mt-4"
            label="Comments"
            path={Undercarriage.DriveLug.Comments}
            inspectionId={externalId}
            inspectionCategoryTypeId={undercarriageCategory?.externalId ?? ''}
            dataLevel={ObservableLevelsEnum.equipmentPart}
            entityId={equipmentPart.id}
            entityExternalId={equipmentPart.externalId}
          />

          <CompleteButton onClick={() => navigate(-1)} />
        </ScreenObservationLayout>
      </div>
    </ObservationDraftScope>
  );
};
